import type { ReactNode } from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  Link as AriaLink,
} from 'react-aria-components';
import { cn, FOCUS_RING } from './cn.ts';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive'
  | 'destructive-secondary';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * `primary` is painted with the ink accent, so its foreground is
 * `text-brand-on-solid` rather than `text-white` — the fill inverts between
 * modes and white-on-near-white is what a literal would produce in dark.
 * `destructive` keeps `text-white`, because a red fill does not invert.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-solid text-brand-on-solid shadow-xs hover:bg-brand-solid-hover data-hovered:bg-brand-solid-hover',
  secondary:
    'bg-primary text-secondary border border-primary shadow-xs hover:bg-primary-hover data-hovered:bg-primary-hover',
  tertiary: 'text-tertiary hover:bg-primary-hover data-hovered:bg-primary-hover',
  destructive:
    'bg-error-solid text-white shadow-xs hover:bg-error-solid-hover data-hovered:bg-error-solid-hover',
  'destructive-secondary':
    'bg-primary text-error-primary border border-error shadow-xs hover:bg-error-primary data-hovered:bg-error-primary',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-sm gap-1.5',
  md: 'h-9 px-3 text-sm gap-2',
  lg: 'h-10 px-3.5 text-sm gap-2',
};

const BASE =
  'inline-flex items-center justify-center rounded-md font-medium transition-colors ' +
  'cursor-pointer select-none whitespace-nowrap ' +
  'data-disabled:cursor-not-allowed data-disabled:bg-disabled data-disabled:text-disabled ' +
  'data-disabled:border-disabled data-disabled:shadow-none';

export interface ButtonProps extends Omit<AriaButtonProps, 'className' | 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <AriaButton
      {...props}
      className={cn(BASE, FOCUS_RING, VARIANTS[variant], SIZES[size], className)}
    >
      {children}
    </AriaButton>
  );
}

/**
 * A link that looks like a button. React Aria's `Link` rather than an anchor
 * with button classes, so it keeps link semantics for a screen reader while
 * picking up the same focus and hover states.
 */
export function LinkButton({
  variant = 'secondary',
  size = 'md',
  className,
  href,
  children,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  href: string;
  children: ReactNode;
}) {
  return (
    <AriaLink
      href={href}
      className={cn(BASE, FOCUS_RING, VARIANTS[variant], SIZES[size], 'no-underline', className)}
    >
      {children}
    </AriaLink>
  );
}
